import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from './notify';
import { ScanLine, Clock, X } from 'lucide-react';
import { VisitCard, money } from './VisitBits';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RadiologyPanel({ visits, refresh, authCfg, role, isSuperAdmin }) {
  const canAct = isSuperAdmin || role === 'Radiology' || role === 'Admin';
  const awaiting = visits.filter((v) => v.status === 'AWAITING_RAD_APPROVAL');
  const pending = visits.filter(
    (v) => ['RAD_PENDING', 'DIAGNOSTICS_PENDING', 'ADMITTED'].includes(v.status)
      && (v.rad_orders || []).some((o) => o.status === 'pending')
  );

  const [active, setActive] = useState(null);
  const [results, setResults] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const openVisit = (visit) => {
    setActive(visit);
    setResults({});
  };

  const complete = async () => {
    const pendingOrders = (active.rad_orders || []).filter((o) => o.status === 'pending');
    const missing = pendingOrders.filter((o) => !(results[o.order_id] || '').trim());
    if (missing.length > 0) {
      toast.error(`Enter a result for every scan (${missing.length} missing)`);
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/visits/${active.visit_id}/rad/complete`,
        { results: pendingOrders.map((o) => ({ order_id: o.order_id, result: results[o.order_id] })) },
        authCfg
      );
      toast.success(`Radiology results recorded${res.data.status === 'ADMITTED' ? ' — patient remains admitted; results sent to the ward doctor' : ` — visit moved to: ${res.data.status.replace(/_/g, ' ').toLowerCase()}`}`);
      setActive(null);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to record radiology results');
    } finally {
      setSubmitting(false);
    }
  };

  if (active) {
    const pendingOrders = (active.rad_orders || []).filter((o) => o.status === 'pending');
    return (
      <Card className="border-sky-200 shadow-md">
        <CardHeader className="bg-gradient-to-r from-sky-50 to-sky-100 border-b border-sky-200 p-4">
          <CardTitle className="flex items-center justify-between text-sky-900 text-base">
            <span className="flex items-center gap-2"><ScanLine className="w-5 h-5" /> Radiology — {active.patient_name}</span>
            <Button variant="outline" size="sm" onClick={() => setActive(null)} data-testid="rad-close">
              <X className="w-4 h-4 mr-1" /> Back to queue
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 space-y-4">
          <div className="text-sm text-gray-600 bg-gray-50 rounded-lg p-3">
            {active.visit_id} • Serial: {active.patient_serial_number}
            {(active.consultation?.provisional_diagnosis || active.consultation?.diagnosis) && <span> • Provisional Dx: <span className="font-medium text-gray-800">{active.consultation.provisional_diagnosis || active.consultation.diagnosis}</span></span>}
          </div>
          {pendingOrders.map((o) => (
            <div key={o.order_id} className="border border-gray-200 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-gray-800">{o.item_name} <span className="font-normal text-gray-500">(x{o.quantity})</span></p>
                <span className="text-sm text-gray-600">{money(active, o.cost * o.quantity)}</span>
              </div>
              <Input
                placeholder="Enter imaging findings / result"
                value={results[o.order_id] || ''}
                onChange={(e) => setResults((prev) => ({ ...prev, [o.order_id]: e.target.value }))}
                className="mt-2"
                data-testid={`rad-result-${o.order_id}`}
              />
            </div>
          ))}
          <Button onClick={complete} disabled={submitting} className="w-full bg-sky-600 hover:bg-sky-700 h-11" data-testid="rad-complete-submit">
            {submitting ? 'Saving…' : 'Submit Results & Complete Radiology'}
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {awaiting.length > 0 && (
        <div>
          <h2 className="text-base font-bold text-gray-800 flex items-center gap-2 mb-2">
            <Clock className="w-5 h-5 text-amber-600" /> Waiting for Approval from Acacia Office ({awaiting.length})
          </h2>
          <div className="space-y-3">
            {awaiting.map((v) => (
              <VisitCard key={v.visit_id} visit={v} showHospital={isSuperAdmin || role === 'ViewOnly'}>
                <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
                  Radiology total ${v.rad_approval?.rad_total_usd} USD exceeds the ${v.rad_approval?.threshold_usd} USD threshold — scans are on hold until the Acacia office approves.
                </div>
              </VisitCard>
            ))}
          </div>
        </div>
      )}

      <div>
        <h2 className="text-base font-bold text-gray-800 flex items-center gap-2 mb-2">
          <ScanLine className="w-5 h-5 text-sky-600" /> Radiology Queue ({pending.length})
        </h2>
        {pending.length === 0 && (
          <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-500" data-testid="rad-empty">
            No radiology orders waiting. Ordered scans will appear here once approved.
          </div>
        )}
        <div className="space-y-3">
          {pending.map((v) => (
            <VisitCard
              key={v.visit_id}
              visit={v}
              showHospital={isSuperAdmin || role === 'ViewOnly'}
              actionLabel={canAct ? `Record Results (${(v.rad_orders || []).filter((o) => o.status === 'pending').length})` : null}
              onAction={canAct ? () => openVisit(v) : null}
              actionTestId={`run-rad-${v.visit_id}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
